import {
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import type { Auth0Client } from "@auth0/auth0-spa-js";

import { getClient } from "./auth0Client";
import { isAuth0Enabled } from "./authConfig";
import { getAuthStatus, setAuthStatus, subscribeAuthStatus } from "./authState";
import AuthBlockedScreen from "@/components/projectCommon/AuthBlockedScreen/AuthBlockedScreen";

const Splash = () => (
  <div className="flex h-lvh justify-center items-center">
    <img
      className="w-[260px]"
      src="/images/logo/harris_questbrand_complete_logo.png"
      alt="Harris poll"
    />
  </div>
);

// Restore appState.returnTo (set at login time), same-origin only, without a
// full reload. `//` is rejected so a protocol-relative URL cannot redirect off
// site.
const onRedirectCallback = (appState?: { returnTo?: string }) => {
  const target = appState?.returnTo;
  if (target && target.startsWith("/") && !target.startsWith("//")) {
    window.history.replaceState({}, document.title, target);
  }
};

// Blocks children until auth0-react finishes init/callback, and mirrors the
// settled authenticated state into authState for the non-React ky layer. Only
// this component writes the `authenticated` phase, so it cannot drift from
// auth0-react's reducer.
const AuthGate = ({ children }: { children: ReactNode }) => {
  const { isLoading, isAuthenticated } = useAuth0();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      setAuthStatus({ phase: "authenticated" });
    }
  }, [isLoading, isAuthenticated]);

  if (isLoading) return <Splash />;
  return <>{children}</>;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [client, setClient] = useState<Auth0Client | null>(null);
  const status = useSyncExternalStore(subscribeAuthStatus, getAuthStatus);

  useEffect(() => {
    if (!isAuth0Enabled()) return;
    let alive = true;
    getClient()
      .then((c) => {
        if (alive) setClient(c);
      })
      .catch((error) => {
        setAuthStatus({
          phase: "failed",
          reason: "CONFIG_INVALID",
          detail: error instanceof Error ? error.message : String(error),
        });
      });
    return () => {
      alive = false;
    };
  }, []);

  // Flag off: transparent passthrough, no Auth0 anything.
  if (!isAuth0Enabled()) return <>{children}</>;

  // Terminal states must be handled here, above the client/auth gating: a bad
  // config makes getClient reject (client stays null), and a redirect failure
  // leaves the user unauthenticated -- in both cases the gates below would hold
  // a splash or blank forever, so the blocking screen would never show.
  if (status.phase === "blocked" || status.phase === "failed") {
    return <AuthBlockedScreen reason={status.reason} />;
  }

  // Flag on but the shared client is not built yet: hold the splash.
  if (!client) return <Splash />;

  return (
    <Auth0Provider client={client} onRedirectCallback={onRedirectCallback}>
      <AuthGate>{children}</AuthGate>
    </Auth0Provider>
  );
};
